import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Download, AlertCircle, ExternalLink } from 'lucide-react';
import {
  listNFFornecedor,
  listNFFornecedorPrestadores,
  NFFornecedor,
} from '../services/apiService';

function fmtBRL(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—';
  return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function fmtCNPJ(cnpj: string): string {
  if (!cnpj || cnpj.length !== 14) return cnpj;
  return `${cnpj.slice(0, 2)}.${cnpj.slice(2, 5)}.${cnpj.slice(5, 8)}/${cnpj.slice(8, 12)}-${cnpj.slice(12)}`;
}

function fmtDate(s: string): string {
  if (!s) return '—';
  const [y, m, d] = s.split('-');
  if (!y || !m || !d) return s;
  return `${d}/${m}/${y}`;
}

export default function NotasFiscaisFornecedorPage() {
  const [nfs, setNfs] = useState<NFFornecedor[]>([]);
  const [prestadores, setPrestadores] = useState<any[]>([]);
  const [stats, setStats] = useState({ total: 0, valor_total: 0, needs_classification: 0 });
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState<number | undefined>(new Date().getFullYear());
  const [month, setMonth] = useState<number | undefined>(undefined);
  const [filter_cnpj, setFilterCnpj] = useState<string>('');
  const [onlyOpen, setOnlyOpen] = useState(false);
  const [error, setError] = useState('');

  const reload = async () => {
    setLoading(true);
    try {
      const [list, prest] = await Promise.all([
        listNFFornecedor({
          year,
          month,
          cnpj_prestador: filter_cnpj || undefined,
          only_open: onlyOpen,
        }),
        listNFFornecedorPrestadores(),
      ]);
      setNfs(list.nfs);
      setStats({
        total: list.total,
        valor_total: list.valor_total,
        needs_classification: list.needs_classification,
      });
      setPrestadores(prest.prestadores);
      setError('');
    } catch (e: any) {
      setError(e?.message || 'Erro ao carregar');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, month, filter_cnpj, onlyOpen]);

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <FileText className="h-6 w-6 text-purple-600" />
          NFs de Fornecedor
        </h1>
      </div>

      <p className="text-sm text-gray-600">
        NFS-e SPED recebidas pela empresa (somos tomadores). Importadas via SEFAZ scheduler ou upload manual.
      </p>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="border rounded p-3 bg-white">
          <div className="text-xs text-gray-500">Total no período</div>
          <div className="text-xl font-bold">{stats.total}</div>
        </div>
        <div className="border rounded p-3 bg-white">
          <div className="text-xs text-gray-500">Valor total</div>
          <div className="text-xl font-bold">{fmtBRL(stats.valor_total)}</div>
        </div>
        <div className="border rounded p-3 bg-white">
          <div className="text-xs text-gray-500">Precisam classificação</div>
          <div className={`text-xl font-bold ${stats.needs_classification > 0 ? 'text-amber-600' : 'text-green-600'}`}>
            {stats.needs_classification}
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="flex items-center gap-3 flex-wrap">
        <label className="text-sm text-gray-600">Ano:</label>
        <select
          value={year || ''}
          onChange={(e) => setYear(e.target.value ? parseInt(e.target.value) : undefined)}
          className="px-2 py-1 border border-gray-300 rounded text-sm"
        >
          <option value="">Todos</option>
          {[2024, 2025, 2026, 2027].map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>

        <label className="text-sm text-gray-600">Mês:</label>
        <select
          value={month || ''}
          onChange={(e) => setMonth(e.target.value ? parseInt(e.target.value) : undefined)}
          className="px-2 py-1 border border-gray-300 rounded text-sm"
          disabled={!year}
        >
          <option value="">Todos</option>
          {[
            'Janeiro','Fevereiro','Março','Abril','Maio','Junho',
            'Julho','Agosto','Setembro','Outubro','Novembro','Dezembro',
          ].map((nome, i) => (
            <option key={i+1} value={i+1}>{nome}</option>
          ))}
        </select>

        <label className="text-sm text-gray-600">Prestador:</label>
        <select
          value={filter_cnpj}
          onChange={(e) => setFilterCnpj(e.target.value)}
          className="px-2 py-1 border border-gray-300 rounded text-sm min-w-[200px]"
        >
          <option value="">Todos ({prestadores.length})</option>
          {prestadores.map((p) => (
            <option key={p.cnpj} value={p.cnpj}>
              {p.nome} ({p.count})
            </option>
          ))}
        </select>

        <label className="text-sm text-gray-600 flex items-center gap-1">
          <input
            type="checkbox"
            checked={onlyOpen}
            onChange={(e) => setOnlyOpen(e.target.checked)}
          />
          Só com Passivo aberto
        </label>
      </div>

      {error && (
        <div className="px-3 py-2 bg-red-50 border border-red-200 text-red-700 text-sm rounded flex gap-1">
          <AlertCircle className="h-4 w-4" /> {error}
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="text-center py-8 text-gray-500">Carregando…</div>
      ) : nfs.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
          Nenhuma NF de fornecedor encontrada.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-3 py-2 text-left">Emissão</th>
                <th className="px-3 py-2 text-left">Competência</th>
                <th className="px-3 py-2 text-left">Nº NF</th>
                <th className="px-3 py-2 text-left">Prestador</th>
                <th className="px-3 py-2 text-right">Valor</th>
                <th className="px-3 py-2 text-left">Conta despesa</th>
                <th className="px-3 py-2 text-left">Passivo</th>
                <th className="px-3 py-2 text-center">Origem</th>
                <th className="px-3 py-2 text-center">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {nfs.map((nf) => (
                <tr key={nf.txn_id} className="hover:bg-gray-50">
                  <td className="px-3 py-2 whitespace-nowrap">{nf.data_emissao ? fmtDate(nf.data_emissao) : fmtDate(nf.date)}</td>
                  <td className="px-3 py-2 whitespace-nowrap font-semibold" title={nf.data_competencia ? `dCompet (XML): ${nf.data_competencia}` : ''}>
                    {nf.data_competencia ? fmtDate(nf.data_competencia) : (nf.competencia || '—')}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs">{nf.numero || '—'}</td>
                  <td className="px-3 py-2">
                    <div className="truncate max-w-xs" title={nf.nome_prestador}>
                      {nf.nome_prestador || '—'}
                    </div>
                    {nf.cnpj_prestador && (
                      <div className="text-xs text-gray-500 font-mono">{fmtCNPJ(nf.cnpj_prestador)}</div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right font-medium">{fmtBRL(nf.valor)}</td>
                  <td className="px-3 py-2 text-xs font-mono">
                    {nf.conta_despesa === 'Despesas:Pendente' ? (
                      <span className="text-amber-600 flex items-center gap-1">
                        <AlertCircle className="h-3 w-3" /> Pendente
                      </span>
                    ) : (
                      nf.conta_despesa.split(':').slice(-2).join(':')
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs font-mono truncate max-w-[150px]" title={nf.conta_passivo}>
                    {nf.conta_passivo.split(':').slice(-2).join(':')}
                  </td>
                  <td className="px-3 py-2 text-center">
                    <span className="text-xs px-2 py-0.5 bg-gray-100 rounded">{nf.ingest_source || 'manual'}</span>
                  </td>
                  <td className="px-3 py-2 text-center">
                    <div className="flex items-center justify-center gap-1">
                      {nf.doc_key && (
                        <a
                          href={`/documentos?key=${encodeURIComponent(nf.doc_key)}`}
                          target="_blank"
                          rel="noreferrer"
                          className="p-1 hover:bg-purple-100 rounded text-purple-700"
                          title="Ver XML"
                        >
                          <Download className="h-4 w-4" />
                        </a>
                      )}
                      <Link
                        to={`/lancamentos?id=${nf.txn_id}`}
                        className="p-1 hover:bg-blue-100 rounded text-blue-700"
                        title="Ver lançamento"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </Link>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
